/**
 * GA4 client-side event emitters — thin, consent-respecting wrappers
 * around `window.gtag`.
 *
 * Consent is governed in index.html (Google Consent Mode defaults
 * denied + Cookiebot bridge updates `ad_storage`/`analytics_storage`
 * on consent events). This module does NOT re-check consent — gtag
 * itself drops / cookieless-pings events based on the current
 * consent state. The ad-blocker guard (`window.gtag` missing) is the
 * only reason a fire can be refused here.
 *
 * All fire-* helpers return `true` iff the event was actually pushed
 * and NEVER throw.
 *
 * ─── Phase 2 commit (c) (Sebastian ruling 2026-10-21) ───────────────
 *
 * `purchase` is NO LONGER fired client-side. The server-side
 * Measurement Protocol listener on BookPaidEvent + GiftCodeMintedEvent
 * is the single source of truth for money events, keyed by
 * transaction_id. The old `fireGa4Purchase` helper has been retired.
 *
 * This module now owns the FUNNEL events (sign_up, figurenbild_generated,
 * book_from_figurenbild, begin_checkout) + a `getGa4Snapshot()` helper
 * that reads client_id, session_id, and the current Cookiebot consent
 * state at checkout-start so the backend can persist it with the order
 * row and the server-side MP listener can gate on consent + attribute
 * to the Google Ads session.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    // Shape must stay compatible with the Window.Cookiebot declaration
    // in src/lib/analytics/posthog.ts (same TypeScript project; both
    // merge into Window). Keep the full category set here; we only
    // read `statistics` + `marketing`.
    Cookiebot?: {
      hasResponse?: boolean;
      consent?: {
        necessary?: boolean;
        preferences?: boolean;
        statistics?: boolean;
        marketing?: boolean;
      };
    };
  }
}

/**
 * GA4 Measurement ID. Hardcoded here (Vite build-time bakes it; no
 * VITE_* env needed) — same value as the `gtag('config', ...)` call
 * in pwa/index.html. If the ID ever changes, update both places.
 */
const GA4_MEASUREMENT_ID = 'G-H6L2224YYV';

export interface Ga4Snapshot {
  readonly clientId: string | null;
  readonly sessionId: string | null;
  readonly consentAnalytics: boolean;
  readonly consentAds: boolean;
}

/**
 * Read gtag's client_id + session_id and Cookiebot's current
 * consent snapshot. Returns a Promise because gtag.get is callback-
 * shape. Resolves within ~gtag's internal frame; a 1s timeout
 * guards against gtag never answering (ad-blocker race).
 *
 * The two consent booleans mirror Cookiebot's categories:
 *   consentAnalytics = Cookiebot.consent.statistics
 *   consentAds       = Cookiebot.consent.marketing
 * Both default to `false` when Cookiebot hasn't resolved yet or when
 * the user explicitly denied.
 *
 * Never throws.
 */
export function getGa4Snapshot(): Promise<Ga4Snapshot> {
  const consent = window.Cookiebot?.consent;
  const consentAnalytics = consent?.statistics === true;
  const consentAds = consent?.marketing === true;

  const gtag = window.gtag;
  if (typeof gtag !== 'function') {
    return Promise.resolve({
      clientId: null,
      sessionId: null,
      consentAnalytics,
      consentAds,
    });
  }

  return new Promise<Ga4Snapshot>((resolve) => {
    let settled = false;
    let clientId: string | null = null;
    let sessionId: string | null = null;
    const settle = () => {
      if (settled) return;
      settled = true;
      resolve({ clientId, sessionId, consentAnalytics, consentAds });
    };
    const timeout = window.setTimeout(settle, 1000);
    try {
      gtag('get', GA4_MEASUREMENT_ID, 'client_id', (id: unknown) => {
        if (typeof id === 'string' && id.length > 0) clientId = id;
        gtag('get', GA4_MEASUREMENT_ID, 'session_id', (sid: unknown) => {
          if (typeof sid === 'string' && sid.length > 0) sessionId = sid;
          else if (typeof sid === 'number') sessionId = String(sid);
          window.clearTimeout(timeout);
          settle();
        });
      });
    } catch {
      window.clearTimeout(timeout);
      settle();
    }
  });
}

/** One GA4 line item on a begin_checkout event. */
export interface Ga4LineItem {
  readonly item_id: string;
  readonly item_name: string;
  readonly item_variant: string;
  readonly price: number;
  readonly quantity: number;
}

/**
 * `sign_up` — GA4 recommended event name. `method` identifies the
 * entry point (figurenbild | book | other).
 */
export function fireGa4SignUp(params: { method: 'book' | 'figurenbild' | 'other' }): boolean {
  const gtag = window.gtag;
  if (typeof gtag !== 'function') return false;
  try {
    gtag('event', 'sign_up', { method: params.method });
    return true;
  } catch {
    return false;
  }
}

/**
 * `figurenbild_generated` — custom event. Fires when the probe-ready
 * result renders successfully in the /figuren route. Figurenbild sits
 * behind FEATURE_PROBECOVER_ENABLED (OFF in prod); this helper is a
 * no-op outside the figuren surface by virtue of its single call site.
 */
export function fireGa4FigurenbildGenerated(params: { probe_id: string }): boolean {
  const gtag = window.gtag;
  if (typeof gtag !== 'function') return false;
  try {
    gtag('event', 'figurenbild_generated', { probe_id: params.probe_id });
    return true;
  } catch {
    return false;
  }
}

/**
 * `book_from_figurenbild` — custom event on the "Daraus ein Buch
 * machen" click. Fires BEFORE the transfer so the funnel captures
 * intent even if the transfer POST fails downstream.
 */
export function fireGa4BookFromFigurenbild(params: { probe_id: string }): boolean {
  const gtag = window.gtag;
  if (typeof gtag !== 'function') return false;
  try {
    gtag('event', 'book_from_figurenbild', { probe_id: params.probe_id });
    return true;
  } catch {
    return false;
  }
}

/** GA4 begin_checkout payload shape. */
export interface Ga4BeginCheckoutEvent {
  readonly value: number;
  readonly currency: string;
  readonly items: readonly Ga4LineItem[];
}

/**
 * `begin_checkout` — fires on the "Kaufen" click in the book review
 * screen, BEFORE the createBookCheckout POST. Fires on both the
 * Stripe and balance-only paths. Idempotency is a non-concern (one
 * click → one event; a reload of the review screen is a legitimate
 * second funnel event).
 */
export function fireGa4BeginCheckout(event: Ga4BeginCheckoutEvent): boolean {
  const gtag = window.gtag;
  if (typeof gtag !== 'function') return false;
  try {
    gtag('event', 'begin_checkout', {
      value: event.value,
      currency: event.currency,
      items: event.items,
    });
    return true;
  } catch {
    return false;
  }
}
